import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit
} from '@angular/core';
import { UntypedFormBuilder } from '@angular/forms';
import {
  AccountsService,
  CreateJournalCommand,
  CurrenciesService,
  JournalSide,
  JournalsService,
  SetJournalStatusCommand
} from '@api-accounting-client';
import { ActivatedRoute } from '@angular/router';
import { Observable } from 'rxjs';
import { DialogService } from '@progress/kendo-angular-dialog';
import { BreadCrumbItem } from '@progress/kendo-angular-navigation';

import { JournalsBaseComponent } from '../journalsBaseComponent';
import { DialogExtendedService, NotificationsExtendedService } from '../../../shared/services';
import { DataResult } from '../../../shared/models/data-result/data-result';
import { JournalEditableGridModel } from '../journalEditableGridModel';
import { parseTenantDate, tenantNow } from '@app/shared/helpers/tenant-time';
@Component({
    selector: 'app-journal-add',
    templateUrl: './journal-add.component.html',
    styleUrls: ['./journal-add.component.scss'],
    standalone: false
})
export class JournalAddComponent
  extends JournalsBaseComponent
  implements OnInit, AfterViewInit, OnDestroy {

  constructor(
    protected formBuilder: UntypedFormBuilder,
    private route: ActivatedRoute,
    protected accountsService: AccountsService,
    private cd: ChangeDetectorRef,
    protected journalsService: JournalsService,
    protected dialogService: DialogService,
    protected currenciesService: CurrenciesService,
    protected dialogExtendedService: DialogExtendedService,
    protected notificationsExtendedService: NotificationsExtendedService,
  ) {
    super(
      formBuilder,
      journalsService,
      dialogService,
      accountsService,
      currenciesService,
      dialogExtendedService,
      notificationsExtendedService,
    );
  }

  onDataSubmit(command: CreateJournalCommand): Observable<any> {
    return this.journalsService.journalsPost(command);
  }

  protected onDeleteRecord(uid: string): Observable<DataResult<any>> {
    return this.journalsService.journalsUidDelete(uid);
  }

  onStatusSubmit(command: SetJournalStatusCommand): Observable<any> {
    return this.journalsService.journalsStatusPatch(command, this.journalUid);
  }

  async ngOnInit(): Promise<void> {
    await super.ngOnInit();
    await this.handleReverseJournal();
    await this.handleDuplicateJournal();

    this.isOnInitLoaded = true;
  }

  ngAfterViewInit(): void {
    this.cd.detectChanges();
  }

  async handleDuplicateJournal(): Promise<void> {
    const journalUid = this.route.snapshot.queryParams.copy;
    if (journalUid) {
      // Get Journal info
      const journal = await this.getJournal(journalUid);

      // Populate form values
      this.form.patchValue({
        date: tenantNow(),
        note: journal.note ?? ''
      });

      this.populateJournalEntries(journal.items);
    }
  }

  async handleReverseJournal(): Promise<void> {
    const journalUid = this.route.snapshot.queryParams.reverse;
    if (journalUid) {
      // Get Journal info
      const journal = await this.getJournal(journalUid);

      // Populate form values
      this.form.patchValue({
        reference: `REV-${journal.reference}`,
        date: parseTenantDate(journal.date),
        note: journal.note ?? ''
      });

      let gridRecord: JournalEditableGridModel;
      this.gridData = [];
      journal.items.forEach((item, index) => {
        gridRecord = {
          rowId: index,
          accountUid: item?.account?.uid,
          accountCode: item?.account?.code,
          accountName: item?.account?.name,
          accountDetails: {
            fullName: item.account?.fullName,
            accountRootType: item.account?.accountRootType,
            accountType: item.account?.accountType?.name,
            accountSubType: item.account?.accountSubType?.name
          },
          remark: item.note,
          debit: 0,
          credit: 0,
          currency: item.currency?.uid,
          rate: item.currencyRate,
          baseAmount: item.baseAmount,
          parityRate: item.parityRate,
          baseAmount2: item.baseAmount2,
          // Reverse sides
          side: item.side === JournalSide.Debit ?
                              JournalSide.Credit :
                              JournalSide.Debit
        };
        // Reverse side amounts
        item.side === JournalSide.Debit
          ? (gridRecord.credit = item.amount)
          : (gridRecord.debit = item.amount);
        this.gridData.push(gridRecord);
      });
    }
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
  }

  public onItemClick(item: BreadCrumbItem): void {
    this.router.navigate(['/' + item.title.toLowerCase()]);
  }
}
